import React, { useRef, useState } from 'react';
import { X, Loader2, Coins, Download } from 'lucide-react';
import type { AuditResponse, AuditDimension } from '../../../../lib/apis_vibe';
import { exportElementAsPng } from '../../../../lib/exportPng';

interface AuditModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: AuditResponse | null;
  loading: boolean;
}

// 雷达图参数
const RADAR_SIZE = 280;
const RADAR_CENTER = RADAR_SIZE / 2;
const RADAR_RADIUS = 110;

// 维度顶点角度（从顶部开始顺时针）
const getAngle = (i: number, total: number) => (2 * Math.PI * i) / total - Math.PI / 2;

// 计算维度顶点坐标
const getPoint = (i: number, total: number, ratio: number) => {
  const angle = getAngle(i, total);
  return {
    x: RADAR_CENTER + RADAR_RADIUS * ratio * Math.cos(angle),
    y: RADAR_CENTER + RADAR_RADIUS * ratio * Math.sin(angle),
  };
};

// 总分等级颜色
const getTierColor = (tier: string): string => {
  switch (tier) {
    case '优秀':
      return '#10B981';
    case '扎实':
      return '#3B82F6';
    case '合格':
      return '#F59E0B';
    case '未成立':
      return '#EF4444';
    default:
      return '#6B7280';
  }
};

// 维度等级颜色
const getDimensionTierColor = (tier: string): string => {
  switch (tier) {
    case '优秀':
      return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
    case '合格':
      return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
    case '未达标':
      return 'text-red-400 bg-red-500/10 border-red-500/30';
    default:
      return 'text-gray-400 bg-gray-500/10 border-gray-500/30';
  }
};

// 雷达图子组件
const RadarChart: React.FC<{ dimensions: AuditDimension[] }> = ({ dimensions }) => {
  const total = dimensions.length;
  if (total === 0) return null;

  // 5 圈同心十边形（2/4/6/8/10 分）
  const rings = [2, 4, 6, 8, 10];
  const ringPolygons = rings.map(r => {
    const ratio = r / 10;
    const points = dimensions.map((_, i) => {
      const p = getPoint(i, total, ratio);
      return `${p.x},${p.y}`;
    }).join(' ');
    return points;
  });

  // 10 条轴线
  const axisLines = dimensions.map((_, i) => {
    const p = getPoint(i, total, 1);
    return { x1: RADAR_CENTER, y1: RADAR_CENTER, x2: p.x, y2: p.y };
  });

  // 数据多边形
  const dataPoints = dimensions.map((d, i) => {
    const ratio = Math.max(0, Math.min(10, d.score)) / 10;
    const p = getPoint(i, total, ratio);
    return `${p.x},${p.y}`;
  }).join(' ');

  // 顶点圆点
  const dataDots = dimensions.map((d, i) => {
    const ratio = Math.max(0, Math.min(10, d.score)) / 10;
    return getPoint(i, total, ratio);
  });

  // 标签
  const labels = dimensions.map((d, i) => {
    const p = getPoint(i, total, 1.18);
    return { ...p, name: d.name, score: d.score };
  });

  return (
    <svg width={RADAR_SIZE + 80} height={RADAR_SIZE + 80} className="mx-auto">
      <g transform="translate(40, 40)">
        {/* 5 圈同心十边形 */}
        {ringPolygons.map((pts, idx) => (
          <polygon
            key={idx}
            points={pts}
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="1"
          />
        ))}
        {/* 10 条轴线 */}
        {axisLines.map((l, i) => (
          <line
            key={i}
            x1={l.x1}
            y1={l.y1}
            x2={l.x2}
            y2={l.y2}
            stroke="rgba(255,255,255,0.15)"
            strokeWidth="1"
          />
        ))}
        {/* 数据多边形填充 */}
        <polygon
          points={dataPoints}
          fill="rgba(168,85,247,0.3)"
          stroke="rgba(168,85,247,0.8)"
          strokeWidth="2"
        />
        {/* 数据顶点圆点 */}
        {dataDots.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="3"
            fill="#A855F7"
            stroke="#fff"
            strokeWidth="1"
          />
        ))}
        {/* 维度标签 */}
        {labels.map((l, i) => (
          <g key={i}>
            <text
              x={l.x}
              y={l.y - 4}
              textAnchor="middle"
              fill="rgba(255,255,255,0.7)"
              fontSize="9"
            >
              {l.name.length > 6 ? l.name.slice(0, 6) + '…' : l.name}
            </text>
            <text
              x={l.x}
              y={l.y + 8}
              textAnchor="middle"
              fill="#A855F7"
              fontSize="10"
              fontWeight="bold"
            >
              {l.score}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
};

const AuditModal: React.FC<AuditModalProps> = ({ isOpen, onClose, result, loading }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!contentRef.current) return;
    setIsExporting(true);
    const el = contentRef.current;
    // 保存原始内联样式
    const originalMaxHeight = el.style.maxHeight;
    const originalOverflow = el.style.overflow;
    // 临时移除滚动限制，让内容完整展开（覆盖 Tailwind 的 max-h-[90vh] 和 overflow-y-auto）
    el.style.maxHeight = 'none';
    el.style.overflow = 'visible';
    try {
      const timestamp = result?.audited_at
        ? new Date(result.audited_at).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10);
      await exportElementAsPng(el, `AI代码评测报告-${timestamp}`);
    } catch (e) {
      console.error('Export failed', e);
      alert('导出失败，请稍后重试');
    } finally {
      // 恢复原始样式（设为空字符串让 Tailwind class 重新生效）
      el.style.maxHeight = originalMaxHeight;
      el.style.overflow = originalOverflow;
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  const evaluation = result?.evaluation;
  const tierColor = evaluation ? getTierColor(evaluation.total_tier) : '#6B7280';
  const isCached = result?.status === 'cached';
  const totalTokens = result ? result.input_tokens + result.output_tokens : 0;
  const totalMoney = result ? (result.input_money || 0) + (result.output_money || 0) : 0;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={contentRef}
        className="w-[900px] max-w-[95vw] max-h-[90vh] overflow-y-auto bg-[#1a1a1a]/95 border border-purple-500/30 rounded-2xl shadow-[0_0_50px_rgba(168,85,247,0.2)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-gradient-to-r from-purple-900/30 to-transparent border-b border-purple-500/20">
          <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
            📊 AI 代码评测报告
          </h2>
          <div className="flex items-center gap-2">
            {!loading && evaluation && (
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-500/10 border border-purple-500/30 text-purple-300 hover:bg-purple-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="导出为 PNG"
              >
                {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                导出 PNG
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Loading 态 */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Loader2 className="w-10 h-10 animate-spin text-purple-400" />
            <p className="text-gray-400 text-sm">AI 评测中，预计 20-40 秒...</p>
          </div>
        )}

        {/* 结果内容 */}
        {!loading && evaluation && (
          <div className="p-6 space-y-6">
            {/* 状态条 */}
            <div className="flex items-center gap-3 text-xs">
              {isCached ? (
                <span className="px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                  缓存命中
                </span>
              ) : (
                <span className="px-2 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300">
                  AI 实时分析
                </span>
              )}
              {result?.audited_at && (
                <span className="text-gray-500">
                  评测时间：{new Date(result.audited_at).toLocaleString('zh-CN')}
                </span>
              )}
            </div>

            {/* 顶部：雷达图 + 总分卡片 */}
            <div className="flex flex-col md:flex-row gap-6 items-center">
              {/* 雷达图 */}
              <div className="flex-shrink-0">
                <RadarChart dimensions={evaluation.dimensions} />
              </div>

              {/* 总分卡片 */}
              <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <div
                  className="relative w-32 h-32 rounded-full border-4 flex flex-col items-center justify-center"
                  style={{ borderColor: tierColor, boxShadow: `0 0 30px ${tierColor}40` }}
                >
                  <span className="text-4xl font-bold" style={{ color: tierColor }}>
                    {evaluation.total_score}
                  </span>
                  <span className="text-xs text-gray-500">/ 100</span>
                </div>
                <div
                  className="px-4 py-1 rounded-full text-sm font-bold"
                  style={{
                    color: tierColor,
                    backgroundColor: `${tierColor}20`,
                    border: `1px solid ${tierColor}50`,
                  }}
                >
                  {evaluation.total_tier}
                </div>
                <p className="text-sm text-gray-300 text-center max-w-xs">
                  {evaluation.summary}
                </p>
              </div>
            </div>

            {/* 维度明细 */}
            <div>
              <h3 className="text-sm font-bold text-purple-300 mb-3 flex items-center gap-2">
                <span className="w-1 h-4 bg-purple-500 rounded-full" />
                10 维度评分明细
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {evaluation.dimensions.map((d, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-lg bg-black/30 border border-white/5"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-white font-medium">{d.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-purple-300">{d.score}</span>
                        <span className="text-xs text-gray-500">/10</span>
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded border ${getDimensionTierColor(d.tier)}`}
                        >
                          {d.tier}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">{d.evidence}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 苏格拉底建议 */}
            {evaluation.suggestions.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-purple-300 mb-3 flex items-center gap-2">
                  <span className="w-1 h-4 bg-purple-500 rounded-full" />
                  苏格拉底式改进建议
                </h3>
                <div className="space-y-3">
                  {evaluation.suggestions.map((s, i) => (
                    <div
                      key={i}
                      className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/30"
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/20 border border-purple-500/40 text-purple-300 text-xs font-bold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <h4 className="text-sm font-bold text-purple-200">{s.title}</h4>
                      </div>
                      <p className="text-xs text-emerald-300 mb-2 ml-8">
                        ✨ {s.highlight}
                      </p>
                      {s.probes.length > 0 && (
                        <ul className="ml-8 space-y-1">
                          {s.probes.map((p, j) => (
                            <li key={j} className="text-xs text-gray-300 flex items-start gap-1">
                              <span className="text-purple-400 mt-0.5">❓</span>
                              <span>{p}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Token 消耗 */}
            <div className="pt-4 border-t border-white/5 flex items-center justify-between text-xs">
              <div className="flex items-center gap-3 text-gray-400">
                <span className="flex items-center gap-1">
                  <Coins className="w-3.5 h-3.5" />
                  {isCached ? (
                    <span className="text-emerald-400">缓存命中（0 消耗）</span>
                  ) : (
                    <>
                      <span>Input: {result?.input_tokens ?? 0}</span>
                      <span>Output: {result?.output_tokens ?? 0}</span>
                      <span className="text-purple-300">
                        总计: {totalTokens} tokens
                      </span>
                    </>
                  )}
                </span>
              </div>
              {!isCached && totalMoney > 0 && (
                <span className="text-yellow-400">
                  费用：¥{totalMoney.toFixed(4)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* 空状态（非 loading 且无 result） */}
        {!loading && !result && (
          <div className="py-24 text-center text-gray-500 text-sm">
            暂无评测结果
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditModal;
